import type { AmbientNPCPlan } from '@/modules/game-domain/world.types';
import type { HeroAnimationFacing, ReadonlyData } from '@/modules/game-domain/render-snapshot';
import { SERVER_NPC_IDLE_CLIPS, type MeshyNpcClip } from './meshy-npc-clips';

export interface AmbientNpcSample {
  x: number; y: number; dx: number; dy: number; moving: boolean;
  clip: MeshyNpcClip; elapsedMs: number;
}
/** Pure projection of the backend's times/route/idle indices. No route choice or client RNG. */
export function sampleAmbientNpcPlan(plan: ReadonlyData<AmbientNPCPlan>, epochUnixMs: number, serverNowMs: number, oneShot = false): AmbientNpcSample | null {
  const points = plan.waypoints;
  if (!points.length || !Number.isFinite(serverNowMs) || !Number.isFinite(plan.periodMs) || plan.periodMs < 0) return null;
  const local = serverNowMs - epochUnixMs + plan.phaseMs;
  const phase = oneShot ? Math.max(0, local) : plan.periodMs > 0 ? ((local % plan.periodMs) + plan.periodMs) % plan.periodMs : 0;
  for (let index = 0; index < points.length; index++) {
    const from = points[index]!;
    const departure = from.atMs + from.holdMs;
    const idle = SERVER_NPC_IDLE_CLIPS[from.idleIndex] ?? 'breathing';
    if (phase >= from.atMs && (phase < departure || points.length === 1)) {
      return { x: from.x, y: from.y, dx: from.facingX, dy: from.facingY, moving: false,
        clip: idle, elapsedMs: (oneShot && index === 0 ? Math.max(0, plan.idleElapsedMs ?? 0) : 0) + Math.max(0, (oneShot || plan.periodMs > 0 ? phase : local) - from.atMs) };
    }
    const to = points[index + 1];
    if (to && phase >= departure && phase < to.atMs && to.atMs > departure) {
      const fraction = (phase - departure) / (to.atMs - departure);
      let began = index;
      while (began > 0 && points[began]!.holdMs === 0) began--;
      const walkStart = points[began]!.atMs + points[began]!.holdMs;
      return { x: from.x + (to.x - from.x) * fraction, y: from.y + (to.y - from.y) * fraction,
        dx: to.x - from.x, dy: to.y - from.y, moving: true, clip: 'walk', elapsedMs: (oneShot ? Math.max(0, plan.walkElapsedMs ?? 0) : 0) + phase - walkStart };
    }
  }
  if (oneShot) {
    const last = points[points.length - 1]!;
    if (phase >= last.atMs) return { x: last.x, y: last.y, dx: last.facingX, dy: last.facingY, moving: false,
      clip: SERVER_NPC_IDLE_CLIPS[last.idleIndex] ?? 'breathing', elapsedMs: Math.max(0, phase - last.atMs) };
  }
  return null; // Invalid/incomplete server plan cannot manufacture a replacement walk.
}
const SCREEN_DIRECTIONS = ['east', 'south-east', 'south', 'south-west', 'west', 'north-west', 'north', 'north-east'] as const;
/** Project a world-space facing vector through the same2:1 isometric transform as actor positions. */
export function npcWorldFacing(dx: number, dy: number, fallback: HeroAnimationFacing = 'south-east'): HeroAnimationFacing {
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.hypot(dx, dy) < 1e-9) return fallback;
  const screenX = dx - dy, screenY = (dx + dy) / 2;
  const angle = Math.atan2(screenY, screenX);
  return SCREEN_DIRECTIONS[(Math.round(angle / (Math.PI / 4)) + 8) % 8]!;
}
